/**
 * Shared helpers for section definitions and components. This file imports
 * nothing from themeRegistry, so section folders can use it without loops.
 */

export function createReview(partial = {}) {
  return {
    id: `review_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    name: "",
    detail: "",
    quote: "",
    rating: 5,
    ...partial,
  };
}

export function createHeroSlide(partial = {}) {
  return {
    id: `slide_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    imageUrl: "/assets/themes/prestige-hero-v2.webp",
    mobileImageUrl: "",
    badge: "",
    title: "",
    subtitle: "",
    ctaText: "",
    ctaLink: "/collection",
    secondaryCtaText: "",
    secondaryCtaLink: "/collection",
    textColor: "",
    buttonColor: "",
    buttonTextColor: "",
    secondaryButtonColor: "",
    secondaryButtonTextColor: "",
    ...partial,
  };
}

/** Theme slides win. Older themes fall back to saved homepage banners, then a single slide. */
export function resolveHeroSlides(settings = {}, banners = []) {
  if (Array.isArray(settings?.slides)) {
    return settings.slides;
  }

  if (Array.isArray(banners) && banners.length > 0) {
    return banners.map((banner, index) =>
      createHeroSlide({
        id: `banner_${banner.id || index}`,
        imageUrl: banner.image && String(banner.image).indexOf("images.unsplash.com") === -1
          ? banner.image
          : "/assets/themes/prestige-hero-v2.webp",
        badge: banner.badge || "",
        title: banner.title || "",
        subtitle: banner.subtitle || "",
        ctaText: banner.cta_text || banner.ctaText || "",
        ctaLink: banner.link || "/collection",
        secondaryCtaText: settings?.secondaryCtaText || "",
        secondaryCtaLink: settings?.secondaryCtaLink || "/collection",
      })
    );
  }

  return [
    createHeroSlide({
      id: "slide_default",
      imageUrl: settings?.imageUrl && String(settings.imageUrl).indexOf("images.unsplash.com") === -1
        ? settings.imageUrl
        : "/assets/themes/prestige-hero-v2.webp",
      badge: settings?.badge || "FEATURED",
      title: settings?.title || "Discover the New Season",
      subtitle: settings?.subtitle || "",
      ctaText: settings?.ctaText || "Shop now",
      ctaLink: settings?.ctaLink || "/collection",
      secondaryCtaText: settings?.secondaryCtaText || "",
      secondaryCtaLink: settings?.secondaryCtaLink || "/collection",
    }),
  ];
}

const colorHint = "Leave empty to use the theme color.";
const SECTION_COLOR_FIELDS = [
  { key: "backgroundColor", label: "Background color", type: "color", hint: colorHint },
  { key: "textColor", label: "Text color", type: "color", hint: colorHint },
  { key: "mutedTextColor", label: "Small text color", type: "color", hint: colorHint },
  { key: "accentColor", label: "Highlight color", type: "color", hint: colorHint },
  { key: "surfaceColor", label: "Card background", type: "color", hint: colorHint },
  { key: "buttonColor", label: "Button color", type: "color", hint: colorHint },
  { key: "buttonTextColor", label: "Button text color", type: "color", hint: colorHint },
];

const SECTIONS_WITH_BUTTONS = {
  hero_banner: true,
  image_with_text: true,
  promo_banners: true,
  featured_products: true,
  newsletter: true,
};

/**
 * Adds the shared color fields (and button colors where the section has
 * buttons) to a section definition, unless it already defines them.
 */
export function withColorFields(definition) {
  const keys = {};
  (definition.fields || []).forEach((field) => {
    keys[field.key] = true;
  });
  SECTION_COLOR_FIELDS.forEach((field) => {
    if (field.key.indexOf("button") === 0 && !SECTIONS_WITH_BUTTONS[definition.type]) return;
    if (!keys[field.key]) definition.fields.push(field);
  });
  return definition;
}
